import { useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Api } from '../adapters/Api';
import type { UserProfile } from '../domain/types';
import { isBetaActive } from '../domain/beta';
import { useI18n } from '../i18n';
import { WbLogo, formatError } from './components';

// Mismo patrón que apps/web/AuthGate: en modo mock se salta el login; en supabase
// pide login (magic link / Google).
export function AuthGate({
  api,
  children
}: {
  api: Api;
  children: (user: UserProfile | null, signOut: () => Promise<void>) => ReactNode;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  const sessionQuery = useQuery({ queryKey: ['session'], queryFn: () => api.getSession(), retry: false });

  const signOut = async () => {
    await api.signOut();
    localStorage.clear();
    await queryClient.invalidateQueries();
    window.location.href = '/';
  };

  const emailMutation = useMutation({
    mutationFn: () => api.signInWithEmail(email),
    onSuccess: () => setMessage(t('auth.checkEmail')),
    onError: (e) => setMessage(formatError(e))
  });
  const googleMutation = useMutation({
    mutationFn: () => api.signInWithGoogle(),
    onError: (e) => setMessage(formatError(e))
  });

  if (sessionQuery.isLoading) {
    return (
      <main className="auth-shell">
        <div className="panel auth-panel"><p className="muted">{t('auth.loadingSession')}</p></div>
      </main>
    );
  }

  if (api.mode !== 'supabase' || sessionQuery.data) {
    return <>{children(sessionQuery.data ?? null, signOut)}</>;
  }

  return (
    <main className="auth-shell">
      <div className="panel auth-panel">
        <div className="brand">
          <WbLogo />
          <div>
            <h2>{t('auth.title')}</h2>
            {/* Beta gratuita: quien llega de "Únete a la beta" no debe toparse
                con "11,99 €/mes · 5 días" antes de entrar (domain/beta.ts) */}
            <span className="muted">{isBetaActive() ? t('auth.betaSubtitle') : t('auth.subtitle')}</span>
          </div>
        </div>
        <p className="muted">{t('auth.intro')}</p>
        <label>
          {t('auth.email')}
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('auth.placeholderEmail')} />
        </label>
        <button className="primary" disabled={!email || emailMutation.isPending} onClick={() => emailMutation.mutate()}>
          {t('auth.sendLink')}
        </button>
        <button disabled={googleMutation.isPending} onClick={() => googleMutation.mutate()}>
          {t('auth.googleLogin')}
        </button>
        {message && <p className="muted">{message}</p>}
      </div>
    </main>
  );
}
